import { useMemo } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { type SharedDocumentSummary } from "~/api/types";
import { Badge } from "~/atoms/badge";
import { useCompanyGroups } from "~/api/swr/useCompanyGroups";
import { useSharedDocuments } from "~/api/v2/useSharedDocuments";
import { getAncestorGroups } from "~/helpers/companyGroups";
import { formatDate } from "~/helpers/date";
import DocumentIcon from "~/molecules/DocumentIcon";

const SharedDocumentCard = ({
  document,
}: {
  document: SharedDocumentSummary;
}) => {
  const { t } = useTranslation();
  const version = document.latestPublishedVersion;

  return (
    <Link
      to={`/shared-documents/${document.documentId}`}
      className={cn(
        "bg-card border-delegation/50 hover:border-delegation relative flex size-37.5 cursor-pointer flex-col overflow-hidden rounded-sm border-2 p-2 shadow-md",
      )}
    >
      <div
        className="bg-delegation/50 pointer-events-none absolute right-0 bottom-0 size-15"
        style={{ clipPath: "polygon(100% 35%, 100% 100%, 0 100%)" }}
      />
      {document.adoption && (
        <div className="absolute top-2 right-2">
          <Badge variant="success">{t(($) => $.sharedDocuments.adopted)}</Badge>
        </div>
      )}
      <div className="size-12 p-2.5">
        <DocumentIcon type={version.fileType} />
      </div>
      <div className="text-secondary-foreground line-clamp-4 flex-1 overflow-hidden text-xs font-medium wrap-break-word hyphens-auto">
        {version.title}
      </div>
      <div className="text-muted-foreground text-[11px]">
        {`${t(($) => $.commons.documentStatus.published)} ${formatDate(version.publishedAt)}`}
      </div>
    </Link>
  );
};

/**
 * One flat row per ancestor that has shared documents with the active group,
 * highest ancestor first. Rendered above the group's own tiers — categories
 * are tenant-scoped, so shared cards are not aligned to category columns.
 */
export const SharedDocumentsChartRows = ({
  sharedByFilterValues,
}: {
  sharedByFilterValues: string[];
}) => {
  const { t } = useTranslation();
  const { data: groups } = useCompanyGroups();
  const { data: sharedDocuments } = useSharedDocuments();

  const rows = useMemo(() => {
    if (!groups || !sharedDocuments) return [];

    const activeGroup = groups.find((group) => group.isActive);
    if (!activeGroup) return [];

    const documentsByGroupId = new Map<number, SharedDocumentSummary[]>();
    for (const document of sharedDocuments) {
      const forGroup = documentsByGroupId.get(document.sharedBy.id) ?? [];
      forGroup.push(document);
      documentsByGroupId.set(document.sharedBy.id, forGroup);
    }

    return getAncestorGroups(groups, activeGroup.id)
      .filter((ancestor) => documentsByGroupId.has(ancestor.id))
      .filter(
        (ancestor) =>
          sharedByFilterValues.length === 0 ||
          sharedByFilterValues.includes(ancestor.id.toString()),
      )
      .map((ancestor) => ({
        group: { id: ancestor.id, name: ancestor.name },
        documents: documentsByGroupId.get(ancestor.id)!,
      }));
  }, [groups, sharedDocuments, sharedByFilterValues]);

  if (rows.length === 0) return null;

  return (
    <div className="flex flex-col">
      {rows.map((row) => (
        <div key={row.group.id} className="bg-delegation/5 flex pl-7">
          <div className="flex w-64 shrink-0 flex-col justify-center pl-4">
            <h3 className="line-clamp-2 pr-4 font-medium wrap-break-word">
              {row.group.name}
            </h3>
            <div className="text-delegation text-sm">
              {t(($) => $.sharedDocuments.sharedDocuments)}
            </div>
          </div>
          <div className="flex flex-wrap gap-4 py-3 pr-7">
            {row.documents.map((document) => (
              <SharedDocumentCard
                key={document.documentId}
                document={document}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
